import { z } from "zod";

/**
 * Dry-run preview for a **company** XLSX upload (PREC-3870) — the company-list twin of
 * `jobTitleScreeningPreviewSchema`. Answers "how much of this file does Signalist already know?"
 * by resolving each row's identifier against the shared `organization` table. Like the job-title
 * preview it is a pure lookup: no provider call, no enrichment, no charge.
 *
 * `organization` is cross-tenant on purpose — "already in Signalist" means the company record
 * exists at all, which is what makes the row cheap, regardless of who first imported it.
 */
export const companyUploadPreviewSchema = z.object({
	/** Total rows submitted (including blanks and duplicates). */
	totalRows: z.coerce.number(),
	/** Rows carrying no usable identifier at all (no domain, no LinkedIn URL, no name). */
	emptyIdentifiers: z.coerce.number(),
	/** Distinct companies after normalisation — the population the two counts below partition. */
	distinctCompanies: z.coerce.number(),
	/** Distinct companies resolved to an existing organization. */
	alreadyKnown: z.coerce.number(),
	/** Distinct companies with no match — these are the ones enrichment has to discover. */
	newCompanies: z.coerce.number(),
});

export type CompanyUploadPreview = z.infer<typeof companyUploadPreviewSchema>;

/** One row of a company upload, reduced to the three identifiers the preview can match on. */
export const companyUploadRowSchema = z.object({
	name: z.string().optional(),
	domain: z.string().optional(),
	linkedinUrl: z.string().optional(),
});

export type CompanyUploadRow = z.infer<typeof companyUploadRowSchema>;
